import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from './firebase';
import { signInWithEmailAndPassword, signOut, createUserWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { doc, setDoc, onSnapshot } from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Listen to user profile changes in Firestore with error handling
        const userDocRef = doc(db, 'users', user.uid);
        const unsubscribeProfile = onSnapshot(
          userDocRef, 
          (doc) => {
            if (doc.exists()) {
              setUserProfile(doc.data());
            } else {
              setUserProfile(null);
            }
            setLoading(false);
          },
          (error) => {
            console.warn('Firestore permission error (this is normal):', error);
            // Set default profile when there's a permission error
            setUserProfile(null);
            setLoading(false);
          }
        );
        
        return () => unsubscribeProfile();
      } else {
        setUserProfile(null);
        setLoading(false);
      }
    });
    
    return () => unsubscribe();
  }, []);

  const signIn = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const signUp = async (email, password, additionalData = {}) => {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    
    // Create user profile in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      email: user.email,
      role: 'user', // Default role
      isMember: false, // Becomes true after payment
      membershipDate: null,
      createdAt: new Date(),
      ...additionalData
    });
    
    return user;
  };

  const logout = () => {
    return signOut(auth);
  };

  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  const updateUserProfile = async (updates) => {
    if (currentUser) {
      const userDocRef = doc(db, 'users', currentUser.uid);
      await setDoc(userDocRef, updates, { merge: true });
    }
  };

  // Helper functions
  const isAdmin = () => {
    return userProfile && userProfile.role === 'admin';
  };

  const isMember = () => {
    return userProfile && (userProfile.isMember === true || userProfile.role === 'admin');
  };

  const getMemberPrice = (originalPrice) => {
    if (isMember()) {
      return Math.round(originalPrice * 0.8); // 20% discount
    }
    return originalPrice;
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    signIn,
    signUp,
    logout,
    resetPassword,
    updateUserProfile,
    isAdmin,
    isMember,
    getMemberPrice
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
